// ===================================
// DASHBOARD PAGE
// src/pages/Dashboard.js
// Updated for AppLayout with new routing paths
// ===================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getSessions, getSessionStats } from '../services/sessionsApi';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    ready: 0,
    active: 0,
    avgScore: 0,
    practiceTime: 0
  });
  const [recentSessions, setRecentSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load stats
        try {
          const statsData = await getSessionStats();
          setStats({
            total: statsData?.total || 0,
            completed: statsData?.completed || 0,
            ready: statsData?.ready || 0,
            active: statsData?.active || 0,
            avgScore: statsData?.avgScore || 0,
            practiceTime: statsData?.practiceTime || 0
          });
        } catch (statsError) {
          console.error('Error loading stats:', statsError);
        }

        // Load recent sessions
        try {
          const sessionsData = await getSessions({ limit: 5 });
          setRecentSessions(Array.isArray(sessionsData) ? sessionsData : []);
        } catch (sessionsError) {
          console.error('Error loading sessions:', sessionsError);
          setRecentSessions([]);
        }

      } catch (err) {
        console.error('Dashboard error:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const formatTime = (minutes) => {
    if (!minutes) return '0 min';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: 'badge-success',
      active: 'badge-warning',
      paid: 'badge-info',
      pending: 'badge-neutral',
      expired: 'badge-error'
    };
    return badges[status] || 'badge-neutral';
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        {/* Welcome Section */}
        <div className="welcome-section">
          <h1>Welcome back, {user?.name || user?.user_name || 'User'}!</h1>
          <p>Track your interview practice progress</p>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {/* Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Total Sessions</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <span className="stat-value">{stats.completed}</span>
              <span className="stat-label">Completed</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">⭐</div>
            <div className="stat-content">
              <span className="stat-value">{stats.avgScore || '-'}</span>
              <span className="stat-label">Avg Score</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">⏱️</div>
            <div className="stat-content">
              <span className="stat-value">{formatTime(stats.practiceTime)}</span>
              <span className="stat-label">Practice Time</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="dashboard-content">
          {/* Recent Sessions */}
          <div className="recent-sessions-card">
            <div className="card-header">
              <h2>Recent Sessions</h2>
              <Link to="/app/sessions" className="view-all-link">View All →</Link>
            </div>

            <div className="sessions-list">
              {recentSessions.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📭</div>
                  <h3>No sessions yet</h3>
                  <p>Book your first interview practice session to get started!</p>
                  <Link to="/app/book" className="btn btn-primary">
                    Book Session
                  </Link>
                </div>
              ) : (
                recentSessions.map((session) => (
                  <div
                    key={session.id}
                    className="session-item"
                    onClick={() => navigate(`/app/sessions/${session.id}`)}
                  >
                    <div className="session-info">
                      <div className="session-type">
                        <span className="type-icon">
                          {session.interview_type === 'voice' ? '🎙️' : session.interview_type === 'phone' ? '📞' : '💬'}
                        </span>
                        <span className="type-name">
                          {session.interviewer_personality || 'Interview'}
                        </span>
                      </div>
                      <div className="session-meta">
                        {session.duration} min • {session.experience_level}
                      </div>
                    </div>

                    <div className="session-right">
                      <span className={`badge ${getStatusBadge(session.status)}`}>
                        {session.status}
                      </span>
                      {session.status === 'completed' && session.overall_score && (
                        <span className="session-score">{session.overall_score}%</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="dashboard-sidebar">
            {/* Quick Actions */}
            <div className="quick-actions-card">
              <h3>Quick Actions</h3>
              <div className="actions-list">
                <Link to="/app/book" className="action-btn">
                  <span className="action-icon">🎯</span>
                  <span>Book New Session</span>
                </Link>
                <Link to="/app/sessions" className="action-btn">
                  <span className="action-icon">📋</span>
                  <span>View All Sessions</span>
                </Link>
                <Link to="/app/progress" className="action-btn">
                  <span className="action-icon">📈</span>
                  <span>View Progress</span>
                </Link>
              </div>
            </div>

            {/* Ready Sessions */}
            {stats.ready > 0 && (
              <div className="ready-sessions-card">
                <div className="ready-icon">🎬</div>
                <h4>Ready to Start</h4>
                <p>You have {stats.ready} session{stats.ready > 1 ? 's' : ''} ready to begin</p>
                <Link to="/app/start-session" className="btn btn-primary">
                  Start Now
                </Link>
              </div>
            )}

            {/* Tips Card */}
            <div className="tips-card">
              <h4>💡 Interview Tip</h4>
              <p>Use the STAR method (Situation, Task, Action, Result) to structure your behavioral interview answers for maximum impact.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
